import { ReadonlyURLSearchParams } from "next/navigation";
import {
  getCookiebotConsentState,
  type CookiebotConsentState,
} from "@/utils/analytics/cookiebot";
import { queueGA4Event } from "@/utils/analytics/ga4EventQueue";

type GtmEventPayload = {
  eventName: string;
  params?: Record<string, unknown>;
};
type GtmEventPushResult = "pushed" | "queued" | "dropped";

const COOKIEBOT_ANALYTICS_REPLAY_EVENTS = [
  "CookiebotOnLoad",
  "CookiebotOnAccept",
  "CookiebotOnConsentChanged",
  "CookiebotOnDecline",
] as const;
const MAX_PENDING_GTM_EVENTS = 100;
let pendingGtmEvents: GtmEventPayload[] = [];
let isGtmEventReplayListenerActive = false;
let pendingGtagUserProperties: Record<string, unknown> | null = null;
let isGtagUserPropertiesReplayListenerActive = false;

const getAnalyticsConsentState = (): CookiebotConsentState => {
  return getCookiebotConsentState("analytics");
};

const pushGtmEventToDataLayer = (
  eventName: string,
  params?: Record<string, unknown>,
) => {
  queueGA4Event({
    eventName,
    eventData: params ?? {},
    hasRequiredConsent: true,
    isDevelopment: process.env.NODE_ENV === "development",
    targetWindow: window,
  });
};

const pushGtagUserProperties = (properties: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag === "function") {
    // User properties are a gtag set command, not a GTM custom event. Keep this
    // exception so configured GA4 user properties continue to use the gtag API.
    window.gtag("set", "user_properties", properties);
  }
};

const removeGtmEventReplayListeners = () => {
  COOKIEBOT_ANALYTICS_REPLAY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, replayPendingGtmEvents);
  });
  isGtmEventReplayListenerActive = false;
};

const removeGtagUserPropertiesReplayListeners = () => {
  COOKIEBOT_ANALYTICS_REPLAY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, replayPendingGtagUserProperties);
  });
  isGtagUserPropertiesReplayListenerActive = false;
};

function replayPendingGtmEvents() {
  const consentState = getAnalyticsConsentState();

  if (consentState === "unknown") {
    // Keep listeners attached while Cookiebot is still loading; the bounded
    // queue is replayed or dropped as soon as Cookiebot exposes a known state.
    return;
  }

  const queuedEvents = pendingGtmEvents.splice(0);

  if (consentState === "granted") {
    queuedEvents.forEach(({ eventName, params }) => {
      pushGtmEventToDataLayer(eventName, params);
    });
  } else if (
    queuedEvents.length > 0 &&
    process.env.NODE_ENV === "development"
  ) {
    console.warn(
      `Dropped ${queuedEvents.length} queued GTM event(s) because Cookiebot analytics consent is denied.`,
    );
  }

  removeGtmEventReplayListeners();
}

function replayPendingGtagUserProperties() {
  const consentState = getAnalyticsConsentState();

  if (consentState === "unknown") {
    return;
  }

  const queuedProperties = pendingGtagUserProperties;
  pendingGtagUserProperties = null;

  if (consentState === "granted" && queuedProperties) {
    pushGtagUserProperties(queuedProperties);
  } else if (queuedProperties && process.env.NODE_ENV === "development") {
    console.warn(
      "Dropped queued GA4 user properties because Cookiebot analytics consent is denied.",
    );
  }

  removeGtagUserPropertiesReplayListeners();
}

const queueGtmEventUntilCookiebotReady = (
  eventName: string,
  params?: Record<string, unknown>,
) => {
  pendingGtmEvents.push({ eventName, params });
  if (pendingGtmEvents.length > MAX_PENDING_GTM_EVENTS) {
    pendingGtmEvents.shift();
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `Dropped oldest queued GTM event because the pending queue exceeded ${MAX_PENDING_GTM_EVENTS} events.`,
      );
    }
  }

  if (isGtmEventReplayListenerActive) {
    return;
  }

  COOKIEBOT_ANALYTICS_REPLAY_EVENTS.forEach((cookiebotEventName) => {
    window.addEventListener(cookiebotEventName, replayPendingGtmEvents);
  });
  isGtmEventReplayListenerActive = true;
};

const pushGtmEvent = (
  eventName: string,
  params?: Record<string, unknown>,
): GtmEventPushResult => {
  if (typeof window === "undefined") {
    return "dropped";
  }

  const consentState = getAnalyticsConsentState();

  if (consentState === "unknown") {
    queueGtmEventUntilCookiebotReady(eventName, params);
    return "queued";
  }

  if (consentState === "denied") {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `GTM ${eventName} event not pushed because Cookiebot analytics consent is denied.`,
      );
    }
    return "dropped";
  }

  if (pendingGtmEvents.length > 0) {
    replayPendingGtmEvents();
  }

  pushGtmEventToDataLayer(eventName, params);
  return "pushed";
};

export const sendConsentGatedAnalyticsEvent = pushGtmEvent;

const queueGtagUserPropertiesUntilCookiebotReady = (
  properties: Record<string, unknown>,
) => {
  pendingGtagUserProperties = {
    ...(pendingGtagUserProperties ?? {}),
    ...properties,
  };

  if (isGtagUserPropertiesReplayListenerActive) {
    return;
  }

  COOKIEBOT_ANALYTICS_REPLAY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, replayPendingGtagUserProperties);
  });
  isGtagUserPropertiesReplayListenerActive = true;
};

const setGtagUserProperties = (properties: Record<string, unknown>) => {
  if (typeof window === "undefined") {
    return;
  }

  const consentState = getAnalyticsConsentState();

  if (consentState === "unknown") {
    queueGtagUserPropertiesUntilCookiebotReady(properties);
    return;
  }

  if (consentState === "denied") {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "GA4 user properties not set because Cookiebot analytics consent is not granted.",
      );
    }
    return;
  }

  pushGtagUserProperties(properties);
};

export const GAClickEvent = (
  label: string,
  action: string = "submit",
) => {
  pushGtmEvent(action, {
    // Keep legacy GA/GTM variable names alongside GA4 names so existing GTM
    // triggers do not break while the container migration rolls out.
    category: "User",
    action,
    label,
    event_category: "User",
    event_label: label,
  });

};

export function GAEvent(
  label: string,
  type: string,
) {
  pushGtmEvent(type, {
    // Keep legacy GA/GTM variable names alongside GA4 names so existing GTM
    // triggers do not break while the container migration rolls out.
    category: "User",
    action: type,
    label,
    event_category: "User",
    event_label: label,
  });

}

export function handleCustomDefinitions(params: ReadonlyURLSearchParams) {
  const angle = params.get("angle");
  const cta = params.get("cta");
  const subject_line = params.get("subjectline");
  const subject = params.get("subject");
  const emailId = params.get("eid");
  const variant = params.get("variant");
  const step = params.get("step");

  if (angle) {
    angle.split("").forEach((i) => {
      sendEventCapture("angle", i);
    });
  }

  if (cta) {
    sendEventCapture("cta", cta);
  }

  if (subject_line) {
    sendEventCapture("subject_line", subject_line);
  }

  if (subject) {
    sendEventCapture("subject", subject);
  }

  if (variant) {
    sendEventCapture("variant", variant);
  }

  if (step) {
    sendEventCapture("step", step);
  }

  if (emailId) {
    sendEventCapture("email_id", emailId);
  }

  const audience = params.get("audience") ?? params.get("type");

  if (audience) {
    setGtagUserProperties({
      audience: audience,
    });
  }
}

export function sendEventCapture(eventName: string, value: string) {
  pushGtmEvent(`${eventName}_capture`, {
    angle: value,
  });

}
